import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useThunk } from "../../hooks/useThunk";
import { fetchTags } from "../../store/tagExports";

import { useSupremeEditsPlayer } from "../../contexts/SupremeEditsContext";

import { IoMdClose } from "react-icons/io";

import CustomDropdown from "./CustomDropdown";
import CustomSliderDual from "./CustomSlider";
import SearchBar from "../Layout/SearchBar";

const keys = [
  "1A",
  "1B",
  "2A",
  "2B",
  "3A",
  "3B",
  "4A",
  "4B",
  "5A",
  "5B",
  "6A",
  "6B",
  "7A",
  "7B",
  "8A",
  "8B",
  "9A",
  "9B",
  "10A",
  "10B",
  "11A",
  "11B",
  "12A",
  "12B",
];

const keyColors = {
  "1A": "#AEFFE1",
  "1B": "#8AFFD8",
  "2A": "#B2EFAD",
  "2B": "#9FFF9D",
  "3A": "#CEF89B",
  "3B": "#B6F974",
  "4A": "#DFE39B",
  "4B": "#D3D074",
  "5A": "#F6BDA3",
  "5B": "#F39B7F",
  "6A": "#FEA3B4",
  "6B": "#FE7E95",
  "7A": "#F3A3CF",
  "7B": "#ED7EB8",
  "8A": "#DEA3EC",
  "8B": "#D37EDB",
  "9A": "#CCA4F3",
  "9B": "#B280FE",
  "10A": "#BCBFFE",
  "10B": "#9AAFFE",
  "11A": "#ACEAFE",
  "11B": "#88E2FE",
  "12A": "#A3FFFB",
  "12B": "#7FFFFA",
};

const GENRES = [
  "All Genres",
  "Hip-Hop / R&B",
  "Latin",
  "Electronic / Dance",
  "International",
  "Reggae / Dancehall",
  "Pop / Top 100",
];

const GENRE_COLORS = {
  "All Genres": "#000000",
  "Hip-Hop / R&B": "#E63946",
  Latin: "#6A0DAD",
  "Electronic / Dance": "#007BFF",
  International: "#FFFF00",
  "Reggae / Dancehall": "#32CD32",
  "Pop / Top 100": "#FF1493",
};

const VERSIONS = [
  "All Versions",
  "Clean",
  "Dirty",
  "Intro Clean",
  "Intro Dirty",
  "Short",
  "Extended",
  "Radio",
  "Acapella",
  "Instrumental",
];

const DECADES = [
  "All Decades",
  "2020s",
  "2010s",
  "2000s",
  "1990s",
  "1980s",
  "1970s",
  "1960s",
  "1950s",
];

const FilterModal = ({
  isVisible,
  onClose,
  searchKeyword = "",
  onKeywordChange = () => {},
}) => {
  const location = useLocation();

  const [animationState, setAnimationState] = useState("closed");
  const [filters, setFilters] = useState(() => {
    const defaultFilters = {
      genre: "All Genres",
      version: "All Versions",
      decade: "All Decades",
      key: [],
      bpm: [50, 160],
      popularity: [0, 100],
      danceability: [0, 100],
      energy: [0, 100],
      valence: [0, 100],
      tags: [],
      keyword: searchKeyword || "",
    };

    try {
      const stored = sessionStorage.getItem("filters");
      if (stored) {
        const parsedFilters = JSON.parse(stored);
        // Ensure all array properties are properly initialized
        return {
          ...defaultFilters,
          ...parsedFilters,
          // Force these to be arrays even if they were somehow corrupted in storage
          key: Array.isArray(parsedFilters.key) ? parsedFilters.key : [],
          tags: Array.isArray(parsedFilters.tags) ? parsedFilters.tags : [],
          bpm: Array.isArray(parsedFilters.bpm) ? parsedFilters.bpm : [50, 160],
          popularity: Array.isArray(parsedFilters.popularity)
            ? parsedFilters.popularity
            : [0, 100],
          danceability: Array.isArray(parsedFilters.danceability)
            ? parsedFilters.danceability
            : [0, 100],
          energy: Array.isArray(parsedFilters.energy)
            ? parsedFilters.energy
            : [0, 100],
          valence: Array.isArray(parsedFilters.valence)
            ? parsedFilters.valence
            : [0, 100],
        };
      }
    } catch (error) {
      console.error("Error parsing filters from session storage:", error);
    }

    // Return default if no stored filters or if parsing failed
    return defaultFilters;
  });

  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [doFetchTags, isLoadingTags, error] = useThunk(fetchTags);

  const modalRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const keyToggleTimeoutRef = useRef(null);
  const pendingToggles = useRef(new Set());

  const { currentTrack } = useSupremeEditsPlayer();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const handleResize = () => {
    setIsMobile(window.innerWidth < 1024);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const fetchTags = async () => {
      const result = await doFetchTags();
      if (result && result.success) {
        setTags(result.response.data || []);
      } else {
        console.error("Failed to fetch tags:", result.error);
      }
    };

    fetchTags();
  }, [doFetchTags]);

  // Sync filter state with updated session storage when modal becomes visible
  useEffect(() => {
    if (isVisible && animationState === "closed") {
      setAnimationState("opening");
      // requestAnimationFrame to ensure smooth animation start
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimationState("open");
        });
      });

      // Update filters after animation starts
      const defaultFilters = {
        genre: "All Genres",
        version: "All Versions",
        decade: "All Decades",
        key: [],
        bpm: [50, 160],
        popularity: [0, 100],
        danceability: [0, 100],
        energy: [0, 100],
        valence: [0, 100],
        tags: [],
        keyword: searchKeyword || "",
      };

      try {
        const stored = sessionStorage.getItem("filters");
        if (stored) {
          const parsedFilters = JSON.parse(stored);
          setFilters({
            ...defaultFilters,
            ...parsedFilters,
            key: Array.isArray(parsedFilters.key) ? parsedFilters.key : [],
            tags: Array.isArray(parsedFilters.tags) ? parsedFilters.tags : [],
            bpm: Array.isArray(parsedFilters.bpm)
              ? parsedFilters.bpm
              : [50, 160],
            popularity: Array.isArray(parsedFilters.popularity)
              ? parsedFilters.popularity
              : [0, 100],
            danceability: Array.isArray(parsedFilters.danceability)
              ? parsedFilters.danceability
              : [0, 100],
            energy: Array.isArray(parsedFilters.energy)
              ? parsedFilters.energy
              : [0, 100],
            valence: Array.isArray(parsedFilters.valence)
              ? parsedFilters.valence
              : [0, 100],
            keyword: searchKeyword || parsedFilters.keyword || "",
          });
        } else {
          setFilters(defaultFilters);
        }
      } catch (e) {
        console.error("Error parsing filters:", e);
        setFilters(defaultFilters);
      }
    } else if (
      !isVisible &&
      (animationState === "open" || animationState === "opening")
    ) {
      setAnimationState("closing");
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      closeTimeoutRef.current = setTimeout(() => {
        setAnimationState("closed");
      }, 300);
    }
  }, [isVisible, animationState, searchKeyword]);

  // Update keyword when searchKeyword prop changes
  useEffect(() => {
    // Always update to match the current searchKeyword prop, even if it's empty
    setFilters((prev) => ({
      ...prev,
      keyword: searchKeyword,
    }));
  }, [searchKeyword]);

  const handleClose = () => {
    if (animationState === "closing" || animationState === "closed") return;
    onClose();
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (keyToggleTimeoutRef.current) {
        clearTimeout(keyToggleTimeoutRef.current);
      }
      pendingToggles.current.clear();
    };
  }, []);

  // const [selectedKeys, setSelectedKeys] = useState([]);

  // useEffect(() => {
  //   if (Array.isArray(filters.key)) {
  //     setSelectedKeys([...filters.key]);
  //   }
  // }, [filters.key]);

  //  const toggleKey = useCallback((key) => {
  //     console.log(`=== DEBUG TOGGLE ${key} ===`);

  //     setSelectedKeys(currentKeys => {
  //       console.log('Current selectedKeys:', currentKeys);

  //       const newKeys = currentKeys.includes(key)
  //         ? currentKeys.filter(k => k !== key)
  //         : [...currentKeys, key];

  //       console.log('New selectedKeys:', newKeys);

  //       setFilters(prev => ({
  //         ...prev,
  //         key: newKeys
  //       }));

  //       return newKeys;
  //     });
  //   }, []);

  const toggleKey = useCallback((key) => {
    console.log(`=== DEBUG TOGGLE ${key} ===`);

    setFilters((prevFilters) => {
      const currentKeys = prevFilters.key || [];
      console.log("CURRENT KEYS BEFORE:", currentKeys);

      const newKeys = currentKeys.includes(key)
        ? currentKeys.filter((k) => k !== key)
        : [...currentKeys, key];

      console.log("NEW KEYS AFTER:", newKeys);

      // Immediately update the UI for iOS
      setTimeout(() => {
        const button = document.querySelector(`[data-key-id="${key}"]`);
        if (button) {
          const isSelected = newKeys.includes(key);
          button.style.opacity = isSelected ? "1" : "0.3";
          button.setAttribute("aria-pressed", isSelected.toString());
          console.log(`Direct DOM update for ${key}: ${isSelected}`);
        }
      }, 0);

      return {
        ...prevFilters,
        key: newKeys,
      };
    });
  }, []);

  console.log("SELECCTED KEYS", filters?.key);

  // const handleKeyButtonClick = useCallback((key, e) => {
  //   e.preventDefault();
  //   e.stopPropagation();

  //   console.log(`Button clicked for ${key}. Current selectedKeys:`, selectedKeys);
  //   toggleKey(key);
  // }, [toggleKey, selectedKeys]);

  const handleKeyButtonClick = useCallback(
    (key, e) => {
      e.preventDefault();
      e.stopPropagation();

      console.log(
        `Button clicked for ${key}. Current filters.key:`,
        filters.key
      );
      toggleKey(key);
    },
    [toggleKey, filters.key]
  );

  const toggleTag = (tagName) => {
    setFilters((prev) => ({
      ...prev,
      tags:
        Array.isArray(prev.tags) && prev.tags.includes(tagName)
          ? prev.tags.filter((t) => t !== tagName)
          : [...(Array.isArray(prev.tags) ? prev.tags : []), tagName],
    }));
  };

  const handleSearch = () => {
    // Ensure we're saving the current filters state to session storage
    // This will ensure keyword value from the current filters is used
    sessionStorage.setItem("filters", JSON.stringify(filters));

    // Make sure the parent component's search keyword is synced
    onKeywordChange(filters.keyword);

    navigate("/search", {
      state: { filters, redirect: location.pathname },
    });
    handleClose();
  };

  const handleClear = () => {
    const cleared = {
      genre: "All Genres",
      version: "All Versions",
      decade: "All Decades",
      key: [],
      bpm: [50, 160],
      popularity: [0, 100],
      danceability: [0, 100],
      energy: [0, 100],
      valence: [0, 100],
      tags: [],
      keyword: "", // Make sure keyword is explicitly cleared
    };
    setFilters(cleared);
    // Update session storage with the cleared filters
    sessionStorage.setItem("filters", JSON.stringify(cleared));
    // Notify the parent component (Navbar) that the keyword has been cleared
    onKeywordChange("");

    // Navigate back to where the user came from
    // If we're already on the search page, stay there but with cleared filters
    if (location.pathname === "/search") {
      navigate("/search", {
        state: { filters: cleared, redirect: location.pathname },
      });
    } else {
      // If we're on a different page, go back to that page
      navigate(location.pathname);
    }
    handleClose();
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        handleClose();
      }
    };

    if (animationState === "open") {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [animationState]);

  if (animationState === "closed") {
    return null;
  }

  const getAnimationClasses = () => {
    switch (animationState) {
      case "opening":
        return "opacity-0 -translate-y-40"; // Original starting position
      case "open":
        return "opacity-100 translate-y-0"; // Final position
      case "closing":
        return "opacity-0 -translate-y-40"; // Back to starting position
      default:
        return "opacity-0 -translate-y-40";
    }
  };

  const getModalHeight = () => {
    if (window.innerWidth < 640) {
      return "85vh";
    }

    if (isMobile) {
      return "80vh";
    }

    return "85vh";
  };

  const getFooterBottom = () => {
    if (window.innerWidth < 640) {
      // Mobile: account for player height (109px) plus bottom nav (52px)
      return currentTrack ? "0px" : "40px";
    }
    return "0"; // Desktop: not fixed, so bottom doesn't matter
  };

  return (
    <div
      ref={modalRef}
      className={`fixed inset-0 transition-all duration-300 ${
        animationState === "open" ? "bg-black/60" : "bg-black/0"
      }`}
      style={{ bottom: isMobile ? "52px" : "0", zIndex: 2147483647 }}
    >
      <div className="absolute inset-0 z-0" onClick={handleClose} />

      <div
        className={`absolute top-0 left-0 right-0 z-10 bg-[#121212] text-white rounded-b-xl transition-all duration-300 ease-out overflow-hidden ${getAnimationClasses()}`}
        style={{
          height: getModalHeight(),
          transform: "translateZ(0)",
          backfaceVisibility: "hidden",
          perspective: "1000px",
        }}
      >
        {/* Header - Fixed at top */}
        <div className="px-3 sm:px-4 lg:px-[30px] py-4 sm:py-7 mx-auto bg-[#121212]">
          <div className="flex justify-between items-center pb-2 sm:pb-4 mb-6">
            <h2 className="text-[16px] sm:text-[18px] lg:text-[20px] leading-[119%] font-public-sans text-[#FFFFFF] font-bold">
              ADVANCED SEARCH
            </h2>
            <button
              onClick={handleClose}
              className="text-xl sm:text-2xl hover:scale-110 transition p-1"
              aria-label="Close advanced search"
            >
              <IoMdClose className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        {/* Scrollable content container */}
        <div
          className="overflow-y-auto overflow-x-hidden"
          style={{
            height: "calc(100% - 180px)", // Account for header and footer height
            scrollbarWidth: "thin",
            scrollbarColor: "#4B4949 transparent",
          }}
        >
          <div
            className="px-3 sm:px-4 lg:px-[30px] mx-auto"
          >
            {/* Content Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-[330px_360px_238px_1fr] gap-x-2 sm:gap-x-4 md:gap-x-10 xl:gap-x-3 2xl:gap-x-6 gap-y-6 sm:gap-y-10">
              {/* Genre / Versions / Decades */}
              <div className="space-y-2 lg:space-y-1">
                {/* Search Bar - Mobile Only */}
                <div className="col-span-full lg:hidden">
                  <SearchBar
                    searchKeyword={filters.keyword}
                    setSearchKeyword={(value) =>
                      setFilters((prev) => ({ ...prev, keyword: value }))
                    }
                    onSearch={(e) => {
                      e.preventDefault();
                      handleSearch();
                    }}
                    onToggleFilter={() => {}}
                    isFocused={false}
                    setIsFocused={() => {}}
                    isMobile={false}
                  />
                </div>
                <CustomDropdown
                  label="Genre"
                  options={GENRES}
                  selected={filters.genre}
                  onChange={(val) =>
                    setFilters((prev) => ({ ...prev, genre: val }))
                  }
                  colors={GENRE_COLORS}
                />
                <CustomDropdown
                  label="Versions"
                  options={VERSIONS}
                  selected={filters.version}
                  onChange={(val) =>
                    setFilters((prev) => ({
                      ...prev,
                      version: val,
                    }))
                  }
                />
                <CustomDropdown
                  label="Decade"
                  options={DECADES}
                  selected={filters.decade}
                  onChange={(val) =>
                    setFilters((prev) => ({
                      ...prev,
                      decade: val,
                    }))
                  }
                />
              </div>

              {/* Musical Key Selector */}
              <div className="space-y-4">
                <label className="text-[14px] sm:text-[16px] block mb-[8px] sm:mb-[10px] ml-0 sm:ml-[9px] text-start leading-[100%] font-public-sans font-semibold text-[#8D8D8D]">
                  Key
                </label>
                <div className="grid grid-cols-6 gap-2 sm:gap-1.5 md:gap-2 lg:gap-3 ml-0 sm:ml-[9px]">
                  {keys.map((key) => {
                    const isSelected = (filters.key || []).includes(key);

                    console.log(
                      `Rendering ${key}: isSelected=${isSelected}, filters.key:`,
                      filters.key,
                    );

                    return (
                      <button
                        key={key}
                        type="button"
                        data-key-id={key} // Direct DOM access
                        onClick={(e) => handleKeyButtonClick(key, e)}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleKeyButtonClick(key, e);
                        }}
                        className={`
													text-xs sm:text-sm lg:text-base font-medium 
													p-1 sm:p-1.5 lg:p-2 aspect-square leading-[100%] 
													rounded-md text-[#1E1E1E] transition-all duration-300
													${isSelected ? "opacity-100" : "opacity-30 hover:opacity-100"}
													cursor-pointer touch-manipulation select-none
													flex items-center justify-center
													min-h-[28px] sm:min-h-[32px] lg:min-h-[36px]
												`}
                        style={{
                          backgroundColor: keyColors[key],
                          WebkitTapHighlightColor: "transparent",
                          touchAction: "manipulation",
                        }}
                        aria-pressed={isSelected}
                        aria-label={`Toggle key ${key}`}
                      >
                        {key}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Sliders */}
              <div className="space-y-2 sm:space-y-6 capitalize ml-2">
                {["bpm", "popularity", "danceability", "energy", "valence"].map(
                  (label) => (
                    <CustomSliderDual
                      key={label}
                      label={label}
                      min={label === "bpm" ? 50 : 0}
                      max={label === "bpm" ? 160 : 100}
                      value={filters[label]}
                      onChange={(val) =>
                        setFilters((prev) => ({
                          ...prev,
                          [label]: val,
                        }))
                      }
                    />
                  ),
                )}
              </div>

              {/* Hashtags */}
              <div className=" bg-[#1E1E1E] rounded w-full h-fit mb-4 sm:mb-0">
                <div className="flex justify-between items-center rounded-t-[5px] pl-[10px] sm:pl-[15px] pr-2 py-[0px] bg-[#303030] h-8 sm:h-10">
                  <label className="text-[12px] sm:text-[14px] font-medium leading-[100%] font-public-sans text-[#8D8D8D]">
                    Filter by Hashtags
                  </label>
                  <button
                    onClick={() =>
                      setFilters((prev) => ({
                        ...prev,
                        tags: [],
                      }))
                    }
                    className="text-[9px] sm:text-[10px] font-normal font-public-sans leading-[100%] text-[#8D8D8D] bg-[#1E1E1E7D] w-[50px] sm:w-[57px] h-[16px] sm:h-[18px] rounded-[3px] hover:text-white"
                    aria-label="Clear all hashtags"
                  >
                    Clear All
                  </button>
                </div>
                <div
                  className="flex flex-col gap-0 w-full max-h-[200px] sm:max-h-[300px] overflow-y-auto"
                  style={{
                    scrollbarWidth: "thin",
                    scrollbarColor: "#4B4949 transparent",
                  }}
                >
                  {isLoadingTags ? (
                    <div className="w-full h-full grid grid-cols-1 grid-rows-1 justify-items-center items-center py-8">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="20px"
                        viewBox="0 -960 960 960"
                        width="20px"
                        fill="#e3e3e3"
                        className="animate-spin opacity-50 sm:w-[24px] sm:h-[24px]"
                      >
                        <path d="M480-34q-92.52 0-173.73-35.08-81.21-35.07-141.66-95.53-60.46-60.45-95.53-141.66Q34-387.48 34-480q0-93.14 35.06-173.88 35.07-80.74 95.5-141.43Q225-856 306.24-891q81.24-35 173.76-35 27.52 0 47.76 20.24Q548-885.53 548-858q0 27.53-20.24 47.76Q507.52-790 480-790q-129.35 0-219.67 90.25Q170-609.49 170-480.25 170-351 260.25-260.5q90.26 90.5 219.5 90.5Q609-170 699.5-260.33 790-350.65 790-480q0-27.52 20.24-47.76Q830.47-548 858-548q27.53 0 47.76 20.24Q926-507.52 926-480q0 92.52-35.06 173.77-35.07 81.25-95.5 141.74Q735-104 654.14-69 573.28-34 480-34Z" />
                      </svg>
                    </div>
                  ) : (
                    tags.map((tag) => (
                      <div
                        key={tag._id}
                        className="flex items-center h-[36px] sm:h-[44px] w-full py-[10px] sm:py-[14px] justify-between px-[0px] rounded-[0px] transition-colors duration-200 hover:bg-[#2A2A2A] group cursor-pointer"
                      >
                        <label className="flex w-full items-center gap-1.5 sm:gap-2 text-[12px] sm:text-[14px] leading-[100%] font-medium cursor-pointer px-2 sm:px-4">
                          <input
                            type="checkbox"
                            checked={
                              Array.isArray(filters.tags) &&
                              filters.tags.includes(tag.tagName)
                            }
                            onChange={() => toggleTag(tag.tagName)}
                            className="relative w-[14px] h-[14px] sm:w-[16px] sm:h-[16px] appearance-none border border-[#8D8D8D] rounded-[2px] cursor-pointer transition-all duration-200 before:content-[''] before:absolute before:inset-[2px] sm:before:inset-[3px] before:w-[4px] sm:before:w-[5px] before:h-[7px] sm:before:h-[9px] before:border-r-[1.5px] sm:before:border-r-[1.7px] before:border-b-[1.5px] sm:before:border-b-[1.7px] before:border-cyan-400 checked:before:top-[1px] checked:before:left-[3px] sm:checked:before:left-[4px] before:rotate-45 before:opacity-0 checked:before:opacity-100"
                          />
                          <span className="flex-1 text-[#8D8D8D] group-hover:text-white transition-colors duration-200 max-w-full truncate">
                            {tag.tagName}
                          </span>
                          <div className="right-0 bg-[#8D8D8D1F] group-hover:text-cyan-200 text-[9px] sm:text-[10px] text-center leading-[100%] items-center py-[3px] sm:py-[4px] h-[14px] sm:h-[16px] w-[44px] sm:w-[51px] rounded-[3px] text-[#8D8D8D] font-normal">
                            {tag.usedIn}
                          </div>
                        </label>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Buttons placed outside the scrollable container so it doesn't overlay content */}
        <div className="absolute bottom-0 left-0 right-0 bg-[#121212] sm:bg-transparent border-t sm:border-t-0 border-[#2A2A2A] px-3 sm:px-4 lg:px-[30px] pt-4 pb-4 sm:pt-0 sm:pb-0 z-20">
          <div className="flex flex-col sm:flex-row justify-center sm:justify-end items-center gap-3 sm:gap-4 sm:pt-8 sm:pb-8">
            <button
              onClick={handleClear}
              className="border border-[#4B4949] text-[#4B4949] hover:text-white w-full sm:w-[163px] h-[44px] sm:h-[51px] px-4 py-2 rounded-[16px] font-medium leading-[100%] font-public-sans hover:bg-[#2a2a2a] transition text-[14px] sm:text-[16px]"
            >
              Clear all
            </button>
            <button
              onClick={handleSearch}
              className="px-6 py-2 w-full sm:w-[160px] h-[46px] sm:h-[53px] rounded-[7px] text-[16px] sm:text-[18px] font-medium leading-[100%] font-public-sans text-[#0A0A0A] hover:brightness-110 transition"
              style={{
                background:
                  "linear-gradient(180deg, #52FAF0 0%, #36BAE0 50%, #197AD0 100%)",
              }}
            >
              Search
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;